import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import downloadIcon from "../../assets/download-icon.svg";
import Card from "../../components/Cards/Card";
import CustomPagination from "../../components/CustomPagination/CustomPagination";
import MainContainer from "../../components/container/MainContainer";
import usePagination from "../../hooks/usePagination";
import { getDetails, postDetails } from "../../services/http/http.service";
import { hideLoader, showLoader } from "../../store/Loader/loader.slice";
import { setApiType, setFilterName, setPageData, setPageNumber } from "../../store/pageData/pageData.slice";
import { setOrderTab } from "../../store/selectedAPIs/selectedAPIs.slice";
import { useAppDispatch, useAppSelector } from "../../store/storeRequests";
import APIList from "../../utils/constants/APIList";
import { message } from "../../utils/constants/Messages";
import StoreSelectors from "../../utils/constants/StoreSelectors.constants";
import { APIResponseCodes } from "../../utils/constants/keys.constants";
import { NavList } from "../../utils/constants/navigation";
import jsonToExcel from "../../utils/helpers/jsonToExcel";
import "./apis-listing.css";

const APIsListing = () => {
  const isUIMounted = useRef(false);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { pageData } = useAppSelector(StoreSelectors.PAGE_STATE);
  const { access } = useAppSelector(StoreSelectors.USER_STATE);

  /** Pass data to pagination hook to create configuration
   * of react-paginate component.... 
   */
  const { currentItems, handlePageClick, itemsPerPage, pageNumber } = usePagination({
    from: location.pathname.includes(NavList.API_LISTING_PRODUCTION) ? "production" : "live",
    items: Array.isArray(pageData) ? pageData : pageData.resApiList,
    perPageResults: pageData.record_per_page,
  });


  /** Handle Tab change */
  const handleTabChange = async (url: "/api-listing/production" | "/api-listing/live") => {
    dispatch(showLoader(message.GETTING_DETAILS))
    navigate(url)
    dispatch(setPageData([]))
    dispatch(setFilterName('All'))

    if (access.hasProdAccess === false) {
      dispatch(hideLoader())
      return
    }

    if (url === NavList.API_LISTING_PRODUCTION) {
      dispatch(setApiType("prod"));
      let response = await getDetails(APIList.GET_ALL_PROD_APIS, () => handleTabChange(url));

      if (response.statusCode === APIResponseCodes.SUCCESS) {
        dispatch(setPageData(response.data));
        dispatch(setPageNumber(0))
      }
      dispatch(hideLoader())
      return
    }

    if (url === NavList.API_LISTING_LIVE) {
      dispatch(setApiType("live"));
      const response = await getDetails(`${APIList.GET_ALL_LIVE_APIS}?page_no=1`, () => handleTabChange(url));
      if (response.statusCode === APIResponseCodes.SUCCESS) {
        dispatch(setPageData(response.data));
        dispatch(setPageNumber(0))
      }
      dispatch(hideLoader())
    }

    navigate(url);
  };

  const getData = async () => {
    if (access.hasProdAccess === false) return
    let response = await getDetails(location.pathname.includes(NavList.API_LISTING_LIVE) ? APIList.GET_ALL_LIVE_APIS + '?page_no=1' : APIList.GET_ALL_PROD_APIS, getData);
    if (response.statusCode === APIResponseCodes.SUCCESS) {
      dispatch(setPageData(response.data));
      dispatch(setPageNumber(0))
    }
  };

  // useEffect(() => window.scrollTo(0, 0), [currentItems]);

  useEffect(() => {
    if (access.hasProdAccess === true) {
      isUIMounted.current = false
    }

    if (isUIMounted.current) return;
    getData();

    isUIMounted.current = true;
  }, [access]);

  const prepareJSONData = (data: any) => {
    if (data.length === 0) return
    const arr: Array<any> = data.map((item: any, index: number) => {
      return {
        0: index + 1,
        1: item?.group_name,
        2: item?.group_version,
        3: item?.api_name,
        4: item?.api_version,
        5: item?.avgResponseTime,
        6: item?.successHits,
        7: item?.failureHits,
      }
    })
    return arr
  }

  /** Download overall performance report */
  const downloadPerformanceReport = async () => {
    dispatch(showLoader(message.GETTING_PERFORMANCE_REPORT))
    try {
      const response = await postDetails(APIList.GET_PERFORMANCE_REPORT, {}, downloadPerformanceReport)
      let temp: any = [];
      if (response.data.lenght !== 0) {
        temp = [...temp, prepareJSONData(response.data)];

      }

      const col_names = [['Sr. No', 'Group Name', 'Group Version', 'API Name', 'API Version', 'Average Response Time', 'Successful Hits', 'Failed Hits']]

      if (response.statusCode === APIResponseCodes.SUCCESS) {
        dispatch(showLoader(message.DOWNLOAD))
        await jsonToExcel(temp, 'MF-Performnce-report', col_names, true)
      }

      dispatch(hideLoader())

    } catch (error) {
      dispatch(hideLoader())
      console.log(error)
    }
  }

  return (
    <MainContainer>
      <div className="mb-5 position-relative">
        {location.pathname === "/api-listing/live" && (
          <button onClick={downloadPerformanceReport} className="download-api-report-btn border-0 primary-bg-color d-flex align-items-center">
            <img width="16" height="16" src={downloadIcon} alt="download report" />
            <p className="mb-0 text-white text-capitalize font-13 weight-400 ps-3">Performance report</p>
          </button>
        )}
        <div className="prod-toggle mx-auto">
          <button
            onClick={() => handleTabChange("/api-listing/production")}
            data-selected={location.pathname === NavList.API_LISTING_PRODUCTION}
            className="prod-toggle-prod border-0 font-14 text-capitalize weight-500"
          >
            Production
          </button>
          <button
            onClick={() => handleTabChange("/api-listing/live")}
            data-selected={location.pathname === NavList.API_LISTING_LIVE}
            className="prod-toggle-live border-0 font-14 text-capitalize weight-500"
          >
            live
          </button>
        </div>

        <div className={location.pathname === NavList.API_LISTING_LIVE ? "pt-5 pt-md-0 pt-lg-0" : ""}>
          {location.pathname === NavList.API_LISTING_PRODUCTION && currentItems?.length !== 0 && <Card from="production" list={currentItems} />}

          {currentItems.length === 0 && location.pathname === NavList.API_LISTING_LIVE && (
            <div className="text-center py-3 no-data-with-view">
              <p className="mb-0 weight-500 font-14 text-primary-color mb-2">No Production orders approved. Approved Production APIs will appear here...</p>
              <button onClick={() => {
                dispatch(setOrderTab('prod'))
                navigate(NavList.ORDERS)
              }} className="border-0 font-13 text-sec-color weight-500">
                View orders {">"}
              </button>
            </div>
          )}

          {currentItems.length === 0 && location.pathname ===  NavList.API_LISTING_PRODUCTION && (
            <div className="text-center py-3 no-data-with-view">
              <p className="mb-0 weight-500 font-14 text-primary-color mb-2">No UAT orders approved. Approved UAT APIs will appear here...</p>
              <button onClick={() => {
                dispatch(setOrderTab('uat'))
                navigate(NavList.ORDERS)
              }} className="border-0 font-13 text-sec-color weight-500">
                View orders {">"}
              </button>
            </div>
          )}

          {location.pathname ===  NavList.API_LISTING_LIVE && <Card from="live" list={typeof currentItems?.length !== 'undefined' ? currentItems : []} />}
        </div>

        {pageData?.total_pages > 1 && <CustomPagination itemsPerPage={itemsPerPage} handlePageClick={handlePageClick} pageCount={pageData?.total_pages} pageNumber={pageNumber - 1} />}
      </div>
    </MainContainer>
  );
};

export default APIsListing;
